'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useConnectorStore } from '@/lib/connectors/store';
import type { ConnectorOperation } from '@/lib/connectors/types';
import {
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  AlertTriangle,
} from 'lucide-react';

interface ConnectorTesterProps {
  open: boolean;
  onClose: () => void;
  connectorId: string;
  operations: ConnectorOperation[];
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-green-500/10 text-green-600',
  POST: 'bg-blue-500/10 text-blue-600',
  PUT: 'bg-yellow-500/10 text-yellow-600',
  PATCH: 'bg-orange-500/10 text-orange-600',
  DELETE: 'bg-red-500/10 text-red-600',
};

export function ConnectorTester({ open, onClose, connectorId, operations }: ConnectorTesterProps) {
  const { testOperation, testResult, testing } = useConnectorStore();

  const [selectedOperation, setSelectedOperation] = useState<string>('');
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [requestBody, setRequestBody] = useState('');
  const [credentials, setCredentials] = useState<Record<string, string>>({});

  const operation = operations.find((op) => op.id === selectedOperation);

  const handleTest = async () => {
    if (!connectorId || !operation) return;

    try {
      await testOperation(connectorId, operation.id, {
        parameters,
        body: requestBody ? JSON.parse(requestBody) : undefined,
        credentials,
      });
    } catch (err) {
      console.error('Test failed:', err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Test Connector</DialogTitle>
          <DialogDescription>
            Test your connector operations with sample data
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6">
          {/* Operation Selection */}
          <div className="space-y-2">
            <Label>Select Operation</Label>
            <Select value={selectedOperation} onValueChange={setSelectedOperation}>
              <SelectTrigger>
                <SelectValue placeholder="Choose an operation to test" />
              </SelectTrigger>
              <SelectContent>
                {operations.map((op) => (
                  <SelectItem key={op.id} value={op.id}>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={METHOD_COLORS[op.method]}>
                        {op.method}
                      </Badge>
                      <span>{op.name}</span>
                      <span className="text-muted-foreground font-mono text-xs">
                        {op.path}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {operation && (
            <>
              {/* Credentials */}
              <div className="space-y-3">
                <Label>Credentials</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">API Key / Token</Label>
                    <Input
                      type="password"
                      value={credentials.api_key || ''}
                      onChange={(e) => setCredentials({ ...credentials, api_key: e.target.value })}
                      placeholder="Enter API key"
                    />
                  </div>
                </div>
              </div>

              {/* Parameters */}
              {(operation.path_parameters?.length || operation.query_parameters?.length) && (
                <div className="space-y-3">
                  <Label>Parameters</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[...(operation.path_parameters || []), ...(operation.query_parameters || [])].map((param) => (
                      <div key={param.name} className="space-y-1">
                        <Label className="text-xs">
                          {param.name}
                          {param.required && <span className="text-destructive">*</span>}
                        </Label>
                        <Input
                          value={parameters[param.name] || ''}
                          onChange={(e) =>
                            setParameters({ ...parameters, [param.name]: e.target.value })
                          }
                          placeholder={param.description || param.name}
                          className="font-mono text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Request Body */}
              {['POST', 'PUT', 'PATCH'].includes(operation.method) && (
                <div className="space-y-2">
                  <Label>Request Body (JSON)</Label>
                  <Textarea
                    value={requestBody}
                    onChange={(e) => setRequestBody(e.target.value)}
                    placeholder='{"key": "value"}'
                    className="font-mono text-sm min-h-[120px]"
                  />
                </div>
              )}

              {/* Test Button */}
              <Button onClick={handleTest} disabled={testing} className="w-full">
                {testing ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Play className="size-4 mr-2" />
                    Run Test
                  </>
                )}
              </Button>

              {/* Results */}
              {testResult && (
                <div className="space-y-4">
                  {/* Status */}
                  <div className="flex items-center gap-4">
                    {testResult.success ? (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="size-5" />
                        <span className="font-medium">
                          {testResult.status_code} OK
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-destructive">
                        <XCircle className="size-5" />
                        <span className="font-medium">Request Failed</span>
                      </div>
                    )}
                    {testResult.latency_ms && (
                      <div className="flex items-center gap-1 text-muted-foreground text-sm">
                        <Clock className="size-4" />
                        {testResult.latency_ms}ms
                      </div>
                    )}
                  </div>

                  {testResult.error && (
                    <Alert variant="destructive">
                      <AlertTriangle className="size-4" />
                      <AlertDescription>{testResult.error}</AlertDescription>
                    </Alert>
                  )}

                  {testResult.success && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Response</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(JSON.stringify(testResult.response, null, 2))}
                        >
                          <Copy className="size-4 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <pre className="p-4 bg-muted rounded-lg overflow-auto max-h-[300px] text-sm font-mono">
                        {JSON.stringify(testResult.response, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
